import { useState } from 'react';
import { PageShell } from '@/components/ui/PageShell';
import { EliteCard } from '@/components/ui/EliteCard';
import { PrimaryButton } from '@/components/ui/Buttons';
import { useRoleStore } from '@/store/roleStore';
import { useAuthStore } from '@/store/authStore';
import { useNavigate } from 'react-router-dom';
import { Key } from 'lucide-react';

export function InviteAcceptPage() {
    const [token, setToken] = useState('');
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [errorMsg, setErrorMsg] = useState('');
    const { acceptInvite } = useRoleStore();
    const { profile } = useAuthStore();
    const navigate = useNavigate();

    const handleAccept = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token.trim() || !profile) return;

        setStatus('loading');
        const res = await acceptInvite(token.trim(), profile.id);

        if (res.success) {
            setStatus('success');
            setTimeout(() => navigate('/team'), 1500);
        } else {
            setStatus('error');
            setErrorMsg(res.error || 'Failed to accept invite');
        }
    };

    return (
        <PageShell title="Join Team" subtitle="Accept Coach Invitation">
            <div className="max-w-md mx-auto mt-8 animate-fade-in-up">
                <EliteCard className="p-6">
                    <div className="flex justify-center mb-6">
                        <div className="w-16 h-16 rounded-2xl bg-gold-500/10 border border-gold-500/20 flex items-center justify-center text-gold-500">
                            <Key size={28} />
                        </div>
                    </div>

                    <h2 className="text-white font-black text-xl text-center mb-2">Enter Invite Token</h2>
                    <p className="text-text-muted text-sm text-center mb-6">
                        Paste the token provided by the athlete's parent to gain access to their profile and stats.
                    </p>

                    <form onSubmit={handleAccept} className="space-y-4">
                        <div>
                            <input
                                type="text"
                                value={token}
                                onChange={e => setToken(e.target.value)}
                                placeholder="e.g. 5x8a9b2c..."
                                className="w-full bg-bg-surface border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold-500/50 transition-colors text-center font-mono tracking-widest"
                            />
                        </div>

                        {status === 'error' && (
                            <div className="text-red-400 text-xs text-center font-bold bg-red-400/10 p-2 rounded">
                                {errorMsg}
                            </div>
                        )}

                        {status === 'success' && (
                            <div className="text-green-400 text-xs text-center font-bold bg-green-400/10 p-2 rounded">
                                Invite accepted! Redirecting...
                            </div>
                        )}

                        <PrimaryButton
                            type="submit"
                            disabled={status === 'loading' || !token.trim()}
                            className="w-full py-3"
                        >
                            {status === 'loading' ? 'Verifying...' : 'Accept Invite'}
                        </PrimaryButton>
                    </form>
                </EliteCard>
            </div>
        </PageShell>
    );
}
